import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Home, PackageCheck, Receipt, Truck } from "lucide-react";
import { Button } from "../components/ui/button";
import SEOHead from "../components/SEOHead";
import { checkoutApi } from "../lib/api";
import { formatDate, formatRupiah } from "../lib/format";
import { initMetaPixel, trackEvent, getGlobalPixelId } from "../lib/metaPixel";

const PaymentSuccessPage = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pixelFired, setPixelFired] = useState(false);

  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      const data = await checkoutApi.getOrder(invoiceId);
      if (data?.payment_status !== "dibayar") {
        navigate(`/payment/${invoiceId}`, { replace: true });
        return;
      }
      setOrder(data);
    } catch {
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [invoiceId, navigate]);

  // Fire Purchase pixel event once order is loaded
  useEffect(() => {
    if (!order || pixelFired) return;
    const pixelId = getGlobalPixelId();
    if (pixelId) {
      initMetaPixel(pixelId);
    }
    // Always track Purchase — pixel is already initialized from LP/checkout page
    const timer = setTimeout(() => {
      trackEvent("Purchase", {
        value: order.total_payment || order.total,
        currency: "IDR",
        content_type: "product",
        contents: (order.items || []).map((item) => ({
          id: String(item.product_id),
          quantity: item.quantity,
        })),
      });
      setPixelFired(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [order, pixelFired]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  if (loading) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-20 text-center">
        <p className="text-stone-600">Memuat konfirmasi pembayaran...</p>
      </main>
    );
  }

  if (!order) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="mb-3 font-display text-3xl font-semibold text-stone-900">
          Pesanan tidak ditemukan
        </h1>
        <Link
          to="/"
          className="inline-flex h-12 items-center rounded-xl bg-emerald-600 px-5 text-white hover:bg-emerald-700"
        >
          Kembali ke Beranda
        </Link>
      </main>
    );
  }

  const totalPaid = order.total_payment || order.total;
  const courierLabel = [
    order.courier_name,
    order.courier_service,
    order.courier_etd,
  ].filter(Boolean).join(" - ");

  return (
    <main className="mx-auto max-w-3xl px-4 py-12 text-center sm:px-6 sm:py-16 lg:px-8">
      <SEOHead
        title="Pembayaran Berhasil"
        description="Halaman konfirmasi pembayaran berhasil untuk pesanan Teras Alami."
        path={invoiceId ? `/sukses/${invoiceId}` : "/sukses"}
        noIndex
      />

      <div className="relative inline-flex">
        <div className="absolute inset-0 rounded-full bg-emerald-200/60 blur-2xl"></div>
        <span className="relative inline-flex h-24 w-24 items-center justify-center rounded-full border border-emerald-100 bg-emerald-50 text-emerald-600">
          <CheckCircle2 className="h-14 w-14" strokeWidth={1.75} />
        </span>
      </div>

      <h1
        className="mt-6 font-display text-4xl font-semibold text-stone-900 sm:text-5xl"
        data-testid="success-title"
      >
        Pembayaran Berhasil
      </h1>
      <p className="mt-3 text-lg text-stone-600">
        Terima kasih, pembayaran Anda sudah terkonfirmasi dan pesanan sedang kami proses.
      </p>

      <div
        className="mt-10 rounded-2xl border border-stone-200 bg-white p-6 text-left shadow-sm sm:p-8"
        data-testid="success-summary"
      >
        <div className="mb-5 flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
            <Receipt className="h-5 w-5" />
          </span>
          <h3 className="font-display text-2xl font-semibold text-stone-900">
            Ringkasan Pesanan
          </h3>
        </div>

        <dl className="grid grid-cols-1 gap-4 text-base sm:grid-cols-2">
          <Info label="No. Invoice" value={order.invoice} />
          <Info label="Tanggal" value={formatDate(order.created_at)} />
          <Info label="Nama Pembeli" value={order.customer.full_name} />
          <Info label="No. HP/WA" value={order.customer.phone} />
          <div className="sm:col-span-2">
            <dt className="text-sm text-stone-500">Produk</dt>
            <dd className="mt-1 font-medium text-stone-900">
              <ul className="space-y-1">
                {(order.items || []).map((item) => (
                  <li key={item.id} className="flex justify-between gap-3">
                    <span>
                      {item.product_name_snapshot} x {item.quantity}
                    </span>
                    <span className="text-stone-600">{formatRupiah(item.subtotal)}</span>
                  </li>
                ))}
              </ul>
            </dd>
          </div>
          <Info label="Kurir" value={courierLabel || "-"} />
          <Info label="Status Pesanan" value={statusText(order.order_status)} />
          <Info label="Nomor Resi" value={order.tracking_number || "Belum tersedia"} />
          <div>
            <dt className="text-sm text-stone-500">Total Dibayar</dt>
            <dd className="font-display text-xl font-semibold text-emerald-700">
              {formatRupiah(totalPaid)}
            </dd>
          </div>
        </dl>

        <div className="mt-6 flex flex-wrap gap-2">
          <StatusPill icon={PackageCheck} text="Pembayaran Terkonfirmasi" />
          <StatusPill icon={Truck} text={order.order_status === "dikirim" ? "Pesanan Dikirim" : "Pesanan Diproses"} />
        </div>
      </div>

      <p className="mx-auto mt-8 max-w-xl text-base text-stone-500">
        Admin akan menyiapkan barang, lalu memperbarui nomor resi ketika paket sudah diserahkan ke ekspedisi.
      </p>

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Button
          asChild
          className="h-14 rounded-xl bg-emerald-600 px-6 text-base text-white hover:bg-emerald-700"
          data-testid="go-history"
        >
          <Link to="/riwayat">
            <Receipt className="mr-2 h-5 w-5" /> Lihat Riwayat Checkout
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
        <Button asChild variant="outline" className="h-14 rounded-xl border-stone-200 px-6 text-base" data-testid="go-home">
          <Link to="/">
            <Home className="mr-2 h-5 w-5" /> Kembali ke Beranda
          </Link>
        </Button>
      </div>
    </main>
  );
};

const statusText = (status) => ({
  menunggu: "Menunggu Pembayaran",
  diproses: "Diproses",
  dikirim: "Dikirim",
  selesai: "Selesai",
  batal: "Batal",
}[status] || status || "-");

const Info = ({ label, value }) => (
  <div>
    <dt className="text-sm text-stone-500">{label}</dt>
    <dd className="font-medium text-stone-900">{value || "-"}</dd>
  </div>
);

const StatusPill = ({ icon: Icon, text }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
    <Icon className="h-4 w-4" />
    {text}
  </span>
);

export default PaymentSuccessPage;
